import * as React from 'react';
import { classNames } from 'utils/ui/css';
import { Stream } from 'utils/ui/media';
import { useMediaPlayback } from 'hooks/useMediaPlayback';

export interface IVideoProps {
  stream: Stream;
  // Stream control props
  autoStart?: boolean;

  // Styling props
  className?: string;
  videoClassName?: string;

  // Event handlers
  onStreamStart?: (stream: Stream) => void;
  onStreamError?: (error: Error) => void;
  onStreamStop?: () => void;
}

/**
 * Video-only renderer for a MediaStream (or a URL). It NEVER plays audio — the element is always
 * muted. Remote audio is rendered by `<AudioSink>`, one per remote at the panel root
 * (`containers/Stream/Voip/RemoteAudioSinks`), so that:
 *   - AVS-4397: the capped tile list (phone auto-cap, `dual` embed) cannot decide who is audible —
 *     a tile that could sink audio would either be heard twice or, the next time someone mounts
 *     `<Video>` without a sink beside it, be the only sink again;
 *   - AVS-4384: a frameless video track (the counterpart's camera is off) stalls this element at
 *     HAVE_NOTHING and cannot take the audio down with it;
 *   - the own-camera preview is never echoed back at the user.
 * The element keeps the FULL stream so it recovers by itself when frames return.
 */
export const Video: React.FC<IVideoProps> = ({ stream, autoStart = true, className, videoClassName }) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const { play, handlePause } = useMediaPlayback(videoRef);

  const assignSource = React.useCallback((): void => {
    const video = videoRef.current;
    if (!video) return;
    if (typeof stream === 'string') {
      video.src = stream;
    } else if (stream instanceof MediaStream) {
      video.srcObject = stream;
    } else {
      // turn off video stream if no stream is provided (e.g. when video is toggled off)
      video.srcObject = null;
    }
    play();
  }, [stream, play]);

  React.useEffect(() => {
    if (autoStart) {
      assignSource();
    }
  }, [autoStart, assignSource]);

  return (
    <div className={classNames('ex3d-video', className)} part="video-container">
      <video
        ref={videoRef}
        className={classNames(videoClassName, '-scale-x-100')}
        muted
        playsInline
        autoPlay
        controls={false}
        onLoadedMetadata={play}
        onPause={handlePause}
      />
    </div>
  );
};
